/**
 * Non-Sequential Template Component
 * Actions can be logged in any order, multiple times
 */
import { Check } from 'lucide-react'
import PropTypes from 'prop-types'
import React from 'react'

import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import { DRILL_STATES } from '@/types/drillTimer'

export function NonSequentialTemplate({ actions = [], onLog, records = [] }) {
  const handleActionClick = (action) => {
    onLog(action, DRILL_STATES.COMPLETED)
  }

  // Count how many times each action has been completed
  const getActionCompletionCount = (actionName) =>
    records.filter(
      (record) => record.action === actionName && record.state === DRILL_STATES.COMPLETED,
    ).length

  return (
    <div className="h-full overflow-y-auto p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {actions.map((action) => {
          const completionCount = getActionCompletionCount(action.action)
          const isCompleted = completionCount > 0

          return (
            <Button
              key={action.id || action.action}
              variant={isCompleted ? 'default' : 'outline'}
              size="lg"
              className={`relative h-20 text-base font-medium ${
                isCompleted ? 'bg-green-600 hover:bg-green-700' : ''
              }`}
              onClick={() => handleActionClick(action.action)}
            >
              <div className="flex flex-col items-center gap-1">
                <div className="flex items-center gap-2">
                  {isCompleted && <Check className="h-4 w-4" />}
                  <span>{action.action}</span>
                </div>
                {completionCount > 1 && (
                  <Badge variant="secondary" className="text-xs">
                    {completionCount}x
                  </Badge>
                )}
              </div>
            </Button>
          )
        })}
      </div>

      {actions.length === 0 && (
        <div className="text-muted-foreground py-8 text-center">
          No actions defined for this drill
        </div>
      )}
    </div>
  )
}

NonSequentialTemplate.propTypes = {
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      action: PropTypes.string.isRequired,
    }),
  ),
  onLog: PropTypes.func.isRequired,
  records: PropTypes.arrayOf(
    PropTypes.shape({
      action: PropTypes.string,
      state: PropTypes.string,
      time: PropTypes.string,
    }),
  ),
}
